import {Evaluator, registerNode} from "@cm/graph-v2-final"
import {AnyImageOpContext, ImageOpNode, ImageOpOutputs} from "@app/textures/texture-editor/operator-stack/image-op-system/detail/abstract/node/image-op-node"
import {ImageOpNodeBag} from "@app/textures/texture-editor/operator-stack/image-op-system/helpers/image-op-node-bag"
import {Vector2} from "@cm/math"
import {definition} from "@app/textures/texture-editor/operator-stack/operators/tiling/node/determine-feature-wavelength/definition"
import {ImageOpNodeTilingFindBestMatchAlongSegment} from "@app/textures/texture-editor/operator-stack/operators/tiling/node/find-best-match-along-segment/node"

@registerNode
export class ImageOpNodeTilingDetermineFeatureWavelength extends ImageOpNode<typeof definition> {
    static nodeClass = "ImageOpNodeTilingDetermineFeatureWavelength"
    static definition = definition

    override dispose(): void {
        this.imageOpNodeBag.dispose()
        super.dispose()
    }

    override run = async (evaluator: Evaluator<AnyImageOpContext>): Promise<ImageOpOutputs<typeof definition>> => {
        const {imageOpHalOptions, sourceImage, rayOrigin, rayTarget, minFeatureLength} = await evaluator.getAll(this.parameters)

        this.imageOpNodeBag.beginFrame()

        const featureWidth = 32
        const searchWindowRatio = 6
        const maxFeatureLength = 128
        const maxCorrelationSamples = 64
        const from = Vector2.fromVector2Like(rayOrigin)
        const to = Vector2.fromVector2Like(rayTarget)
        const dir = to.sub(from).normalized()
        const segmentLength = Vector2.distance(from, to)
        const featureRescale = Math.min(1, maxFeatureLength / segmentLength)
        const searchRescale = featureRescale * searchWindowRatio
        const referenceSegmentLength = Math.round(segmentLength * featureRescale)
        const searchSegmentLength = Math.round(segmentLength * searchRescale)
        const correlationResultSize = searchSegmentLength - referenceSegmentLength + 1
        const stride = Math.max(1, Math.round(correlationResultSize / maxCorrelationSamples))
        const referenceSegment = {
            from: from,
            to: from.add(dir.mul(segmentLength * featureRescale)),
            width: featureWidth,
        }
        const searchSegment = {
            from: from,
            to: from.add(dir.mul(segmentLength * searchRescale)),
            width: featureWidth,
        }
        const bestMatchAlongSegmentNode = this.imageOpNodeBag.getNode("FindBestMatchAlongSegment", ImageOpNodeTilingFindBestMatchAlongSegment, {
            imageOpHalOptions,
            sourceImage: sourceImage,
            referenceSegment,
            searchSegment,
            stride,
        })
        const bestMatchAlongSegment = await evaluator.get(bestMatchAlongSegmentNode)
        // let's try to fold the correlation data by different lengths to find the ideal wave length
        const correlationMean = bestMatchAlongSegment.correlationData.reduce((a, b) => a + b, 0) / bestMatchAlongSegment.correlationData.length
        let bestResult: {waveLength: number; energy: number} | undefined = undefined
        const minWaveLengthInSamples = Math.ceil(minFeatureLength / stride)
        for (let waveLengthInSamples = minWaveLengthInSamples; waveLengthInSamples < bestMatchAlongSegment.correlationData.length; waveLengthInSamples++) {
            let sum = 0
            for (let i = 0; i < bestMatchAlongSegment.correlationData.length; i += waveLengthInSamples) {
                sum += bestMatchAlongSegment.correlationData[i] - correlationMean
            }
            const energy = sum
            if (!bestResult || energy > bestResult.energy) {
                bestResult = {waveLength: waveLengthInSamples * stride, energy}
            }
        }

        this.imageOpNodeBag.endFrame()

        if (!bestResult) {
            throw new Error("no result found")
        }
        return {
            ...bestResult,
            details: bestMatchAlongSegment,
        }
    }

    private imageOpNodeBag = new ImageOpNodeBag("ImageOpNodeTilingDetermineFeatureWavelength")
}
